import React, { Fragment, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { colors, mixins } from '../../core/styles/theme/default';
import i18n from './digitID-i18n';
import { EchIconCheck } from '../../primitives/IconSVG';
import { Hidden, Spacer } from '../../helpers';
import { Button } from '../..//primitives';
import { EchIconDownload } from '../../primitives/IconSVG';
import { CSVLink } from 'react-csv';

const StyledCSVLink = styled(CSVLink)`
  color: ${colors.purple800};
  font-size: ${rem(16)};
  font-weight: 500;
  line-height: 1;
  text-decoration: none !important;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;

  span {
    border-bottom: ${rem(1)} solid currentColor;
  }

  svg {
    margin-left: ${rem(2)};
    vertical-align: middle;
  }

  &:hover,
  &:focus {
    span {
      border-bottom: none;
    }
  }
`;

const StyledSearchForm = styled.form`
  position: relative;
  display: block;
  height: ${rem(56)};

  input {
    height: 100%;
    width: 100%;
    border: ${rem(1)} solid ${colors.purple200};
    border-radius: ${rem(2)};
    font-size: ${rem(16)};
    padding: ${rem(11)} ${rem(73)} ${rem(11)} ${rem(17)};
    color: ${colors.purple800};
    text-overflow: ellipsis;

    &:focus {
      outline: none;
      box-shadow: ${rem(-1)} ${rem(4)} ${rem(8)} ${rem(-2)} rgba(0, 0, 0, 0.15);
    }

    &::placeholder {
      color: ${colors.grey900};
    }
  }
`;

const StyledSearchButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
  border: ${rem(1)} solid ${colors.purple200};
  border-radius: 0 ${rem(2)} ${rem(2)} 0;
  height: 100%;
  width: ${rem(56)};
  min-width: 0;
  padding: 0;
  color: ${colors.white};

  &:focus {
    position: absolute;
  }

  svg {
    margin: 0;

    path {
      stroke: ${colors.white};
    }
  }

  span {
    ${mixins.visuallyHidden};
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const StyledTr = styled.tr`
  border-bottom: ${rem(1)} solid ${colors.purple400};
  vertical-align: top;
`;

const StyledTh = styled.th(
  ({ centered }: { centered?: boolean }) => css`
    text-align: left;
    color: ${colors.black};
    font-size: ${rem(14)};
    font-weight: 400;
    line-height: ${rem(36)};
    padding: 0;

    &:nth-of-type(1) {
      width: 58%;
    }

    &:nth-of-type(2) {
      width: 18%;
      min-width: ${rem(70)};
    }

    &:nth-of-type(3),
    &:nth-of-type(4) {
      width: 12%;
      min-width: ${rem(42)};
    }

    ${centered &&
    css`
      text-align: center;
    `}
  `,
);

const StyledTd = styled.td(
  ({ centered }: { centered?: boolean }) => css`
    text-align: left;

    ${mixins.responsiveStyles({
      padding: {
        zero: `${rem(10)} 0`,
        medium: `${rem(7)} 0`,
      },
      'font-size': {
        zero: 14,
        medium: 18,
      },
      'line-height': {
        zero: 24,
        medium: 30,
      },
    })}

    &:nth-of-type(1) {
      ${mixins.responsiveStyles({
        'padding-right': {
          zero: 15,
          medium: 30,
        },
      })}
    }

    ${centered &&
    css`
      text-align: center;
    `}

    a {
      text-decoration: none;
      color: ${colors.purple800};
      font-weight: 500;
      border-bottom: ${rem(1)} solid currentColor;
      word-break: break-word;

      &:hover,
      &:focus {
        border-bottom: none;
      }
    }
  `,
);

const StyledCheck = styled(EchIconCheck)`
  width: ${rem(19)};
  height: ${rem(15)};
`;

const StyledDash = styled.span`
  position: relative;
  display: inline-block;
  width: ${rem(12)};
  height: ${rem(15)};

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    height: ${rem(2)};
    background-color: #cdcdcd;
  }
`;

const DigitIDRegister = ({
  locale,
  providers,
}: {
  locale: string;
  providers: {
    company: string;
    website: string;
    ID: string[];
    CPO: boolean[];
    EMP: boolean[];
  }[];
}) => {
  const [filteredProviders, setFilteredProviders] = useState(providers);

  const searchProviders = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const search = (e.target as HTMLFormElement).querySelector('input')?.value;

    if (search) {
      const searchValue = search.toLowerCase().trim();

      setFilteredProviders(
        providers.filter(({ company, ID }) =>
          `${company}${ID.join('')}`.toLowerCase().includes(searchValue),
        ),
      );
    } else {
      setFilteredProviders(providers);
    }
  };

  return (
    <>
      <StyledCSVLink
        data={[
          [i18n.company[locale], i18n.website[locale], 'ID', 'CPO', 'EMP'],
          ...providers.map(({ company, website, ID, CPO, EMP }) => [
            company,
            website,
            ID.join('\r\n'),
            CPO.map((item) => (item ? '✓' : '−')).join('\r\n'),
            EMP.map((item) => (item ? '✓' : '−')).join('\r\n'),
          ]),
        ]}
        separator={';'}
        filename="ID-Register.csv"
      >
        <span>ID-Register (CSV)</span>
        <EchIconDownload width="18px" height="18px" />
      </StyledCSVLink>
      <Hidden above="medium">
        <Spacer space="delta" />
      </Hidden>
      <Hidden below="medium">
        <Spacer space="epsilon" />
      </Hidden>
      <StyledSearchForm noValidate onSubmit={searchProviders}>
        <input placeholder={i18n.searchPlaceholder[locale]} />
        <StyledSearchButton
          buttonText={i18n.search[locale]}
          iconBefore="EchIconMagnify"
          type="submit"
        />
      </StyledSearchForm>
      <Hidden above="medium">
        <Spacer space="delta" />
      </Hidden>
      <Hidden below="medium">
        <Spacer space="theta" />
      </Hidden>
      <StyledTable>
        <thead>
          <StyledTr>
            <StyledTh>{i18n.company[locale]}</StyledTh>
            <StyledTh>ID</StyledTh>
            <StyledTh centered>CPO</StyledTh>
            <StyledTh centered>EMP</StyledTh>
          </StyledTr>
        </thead>
        <tbody>
          {filteredProviders.map(
            ({ website, company, ID, CPO, EMP }, index) => (
              <StyledTr key={index}>
                <StyledTd>
                  <a href={website} target="_blank" rel="noreferrer">
                    {company}
                  </a>
                </StyledTd>
                <StyledTd>
                  {ID.map((id, i) => (
                    <Fragment key={i}>
                      {id}
                      {ID.length > 1 && <br />}
                    </Fragment>
                  ))}
                </StyledTd>
                <StyledTd centered>
                  {CPO.map((cpo, i) => (
                    <Fragment key={i}>
                      {cpo ? <StyledCheck /> : <StyledDash />}
                      {CPO.length > 1 && <br />}
                    </Fragment>
                  ))}
                </StyledTd>
                <StyledTd centered>
                  {EMP.map((emp, i) => (
                    <Fragment key={i}>
                      {emp ? <StyledCheck /> : <StyledDash />}
                      {EMP.length > 1 && <br />}
                    </Fragment>
                  ))}
                </StyledTd>
              </StyledTr>
            ),
          )}
        </tbody>
      </StyledTable>
    </>
  );
};

export default DigitIDRegister;
